import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './careReportFormPage.css';
import { Button } from './Button';
import { ShiftsService } from '../services/supabase/shiftsService';
import { CareReportService } from '../services/supabase/careReportService';
import { CareReportSubmissionService } from '../services/supabase/careReportSubmissionService';
import { ReceiptStorageService } from '../services/supabase/receiptStorageService';

function formatShiftOption(shift) {
  const date = new Date(shift.start);
  const dayStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const startTime = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const endTime = new Date(shift.end).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${shift.client} — ${dayStr}, ${startTime} – ${endTime}`;
}

function formatDisplayTime(datetimeStr) {
  return new Date(datetimeStr).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });
}

// datetime-local inputs require "YYYY-MM-DDTHH:MM"
function toDatetimeLocal(datetimeStr) {
  if (!datetimeStr) return '';
  return datetimeStr.substring(0, 16);
}

export default function CareReportFormPage({ user, isAdmin, caretakerList }) {
  const location = useLocation();
  const [shifts, setShifts] = useState([]);
  const [selectedShiftId, setSelectedShiftId] = useState(location.state?.shiftId ?? '');
  const [selectedShift, setSelectedShift] = useState(null);
  const [customQuestions, setCustomQuestions] = useState([]);
  const [mileage, setMileage] = useState('');
  const [suppliesDesc, setSuppliesDesc] = useState('');
  const [suppliesAmount, setSuppliesAmount] = useState('');
  const [importantInfo, setImportantInfo] = useState('');
  const [customAnswers, setCustomAnswers] = useState({});
  const [tasks, setTasks] = useState('');
  const [receiptUrls, setReceiptUrls] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [statusIsError, setStatusIsError] = useState(false);

  // Edit time modal state
  const [editingField, setEditingField] = useState(null); // 'start' | 'end' | null
  const [editingValue, setEditingValue] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Load caregiver's recent shifts on mount
  useEffect(() => {
    if (!user?.email) return;
    const now = Date.now();
    const start = now - 14 * 24 * 60 * 60 * 1000;
    const end = now + 1 * 24 * 60 * 60 * 1000;
    const fetchShifts = isAdmin
      ? ShiftsService.getShiftsForDateRange(start, end)
      : ShiftsService.getShiftsForCaretakerInRange(user.email, start, end);

    fetchShifts.then(rows => {
        const sorted = rows.sort((a, b) => b.start_time_secs - a.start_time_secs);
        const mapped = sorted.map(row => ({
          docID: row.id,
          client: row.client,
          caretaker: row.caretaker,
          caretakerEmail: row.caretaker_email,
          start: row.start_time,
          end: row.end_time,
          editedStart: row.edited_start_time || '',
          editedEnd: row.edited_end_time || '',
          startTimeSecs: row.start_time_secs,
        }));
        setShifts(mapped);
      })
      .catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // When shift selection changes (or shifts list loads), load template + any existing report
  useEffect(() => {
    if (!selectedShiftId) {
      setSelectedShift(null);
      setCustomQuestions([]);
      resetFormFields();
      return;
    }
    const shift = shifts.find(s => s.docID === selectedShiftId);
    if (!shift) return; // shifts not yet loaded
    setSelectedShift(shift);
    setLoading(true);
    setStatusMsg('');

    Promise.all([
      CareReportService.getTemplateForClient(shift.client),
      CareReportSubmissionService.getReportForShift(selectedShiftId),
    ])
      .then(([template, existingReport]) => {
        setCustomQuestions(template?.custom_questions ?? []);
        if (existingReport) {
          setMileage(existingReport.mileage > 0 ? existingReport.mileage : '');
          setSuppliesDesc(existingReport.supplies_description ?? '');
          setSuppliesAmount(existingReport.supplies_amount ?? '');
          setImportantInfo(existingReport.important_information ?? '');
          setCustomAnswers(existingReport.custom_answers ?? {});
          setTasks(existingReport.tasks ?? '');
          setReceiptUrls(existingReport.receipt_urls ?? []);
          setPendingFiles([]);
        } else {
          resetFormFields();
        }
      })
      .catch(() => {
        setStatusMsg('Failed to load report data.');
        setStatusIsError(true);
      })
      .finally(() => setLoading(false));
  }, [selectedShiftId, shifts]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetFormFields = () => {
    setMileage('');
    setSuppliesDesc('');
    setSuppliesAmount('');
    setImportantInfo('');
    setCustomAnswers({});
    setTasks('');
    setReceiptUrls([]);
    setPendingFiles([]);
  };

  const caregiverName = () => {
    const match = caretakerList?.find(c => c.email === user?.email);
    return match?.name ?? user?.email ?? '';
  };

  const handleCustomAnswer = (questionId, value) => {
    setCustomAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  // Open the edit time modal for 'start' or 'end'
  const openEditModal = (field) => {
    const current = field === 'start'
      ? (selectedShift.editedStart || selectedShift.start)
      : (selectedShift.editedEnd || selectedShift.end);
    setEditingField(field);
    setEditingValue(toDatetimeLocal(current));
    setEditError('');
  };

  const closeEditModal = () => {
    setEditingField(null);
    setEditingValue('');
    setEditError('');
  };

  const handleSaveEditedTime = async () => {
    if (!editingValue) {
      setEditError('Please enter a date and time.');
      return;
    }
    // Normalize to full ISO string (seconds = 00)
    const isoValue = editingValue.length === 16 ? editingValue + ':00' : editingValue;

    // Validate start < end
    const currentStart = editingField === 'start' ? isoValue : (selectedShift.editedStart || selectedShift.start);
    const currentEnd   = editingField === 'end'   ? isoValue : (selectedShift.editedEnd   || selectedShift.end);
    if (new Date(currentEnd) <= new Date(currentStart)) {
      setEditError('Shift end time must be after shift start time.');
      return;
    }

    setEditSaving(true);
    setEditError('');
    try {
      const updates = editingField === 'start'
        ? { edited_start_time: isoValue, original_start_time: selectedShift.start }
        : { edited_end_time: isoValue, original_end_time: selectedShift.end };

      await ShiftsService.editTimeWorked(selectedShiftId, updates);

      // Update local selectedShift so the display refreshes
      setSelectedShift(prev => ({
        ...prev,
        editedStart: editingField === 'start' ? isoValue : prev.editedStart,
        editedEnd: editingField === 'end' ? isoValue : prev.editedEnd,
      }));

      closeEditModal();
    } catch {
      setEditError('Failed to save. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleReceiptSelect = (e) => {
    const files = Array.from(e.target.files);
    const newItems = files.map(file => ({ file, previewUrl: URL.createObjectURL(file) }));
    setPendingFiles(prev => [...prev, ...newItems]);
    e.target.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => {
      const next = [...prev];
      URL.revokeObjectURL(next[index].previewUrl);
      next.splice(index, 1);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!selectedShiftId || !selectedShift) {
      setStatusMsg('Please select a shift first.');
      setStatusIsError(true);
      return;
    }
    setLoading(true);
    setStatusMsg('');
    try {
      // Upload photos individually — failures are isolated so they don't block the save
      let allReceiptUrls = [...receiptUrls];
      let photoUploadFailed = false;
      if (pendingFiles.length > 0) {
        setStatusMsg('Uploading receipts…');
        const results = await Promise.allSettled(
          pendingFiles.map(pf => ReceiptStorageService.uploadReceipt(selectedShiftId, pf.file))
        );
        const successfulUrls = results
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value);
        const failures = results.filter(r => r.status === 'rejected');
        if (failures.length > 0) {
          console.error('[Receipt upload] failures:', failures.map(f => f.reason));
        }
        photoUploadFailed = failures.length > 0;
        allReceiptUrls = [...allReceiptUrls, ...successfulUrls];
      }

      const mileageValue = parseFloat(mileage) || 0;

      await Promise.all([
        CareReportSubmissionService.submitReport({
          shift_id: selectedShiftId,
          client_name: selectedShift.client,
          caretaker_email: user.email,
          shift_start: selectedShift.editedStart || selectedShift.start,
          shift_end: selectedShift.editedEnd || selectedShift.end,
          mileage: mileageValue,
          supplies_description: suppliesDesc,
          supplies_amount: parseFloat(suppliesAmount) || 0,
          important_information: importantInfo,
          tasks,
          custom_answers: customAnswers,
          receipt_urls: allReceiptUrls,
        }),
        ShiftsService.editShiftInfo(selectedShiftId, { shift_mileage: mileageValue }),
      ]);
      setPendingFiles([]);
      setReceiptUrls(allReceiptUrls);
      if (photoUploadFailed) {
        setStatusMsg('Report saved, but one or more photos failed to upload. Try adding them again.');
        setStatusIsError(true);
      } else {
        setStatusMsg('Report submitted successfully.');
        setStatusIsError(false);
      }
    } catch {
      setStatusMsg('Failed to submit report. Please try again.');
      setStatusIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const renderQuestionInput = (q) => {
    const value = customAnswers[q.id] ?? '';
    switch (q.type) {
      case 'textarea':
        return (
          <textarea
            className="careFormTextarea"
            value={value}
            onChange={e => handleCustomAnswer(q.id, e.target.value)}
            rows={3}
          />
        );
      case 'checkbox':
        return (
          <select
            className="careFormSelect"
            value={value}
            onChange={e => handleCustomAnswer(q.id, e.target.value)}
          >
            <option value="">— select —</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        );
      case 'number':
        return (
          <input
            type="number"
            className="careFormInput"
            value={value}
            onChange={e => handleCustomAnswer(q.id, e.target.value)}
          />
        );
      default:
        return (
          <input
            type="text"
            className="careFormInput"
            value={value}
            onChange={e => handleCustomAnswer(q.id, e.target.value)}
          />
        );
    }
  };

  const displayStart = selectedShift
    ? (selectedShift.editedStart || selectedShift.start)
    : null;
  const displayEnd = selectedShift
    ? (selectedShift.editedEnd || selectedShift.end)
    : null;

  return (
    <div className="careReportFormPageDiv">
      <div className="careReportFormHeader">
        <Link to="/" className="backToHomeLink">← Back to Home</Link>
        <h2 className="careReportFormTitle">Care Report</h2>
      </div>

      {/* Shift selector */}
      <div className="formSep"></div>
      <div className="careFormSection">
        <label htmlFor="shiftSelect" className="careFormLabel">Select Shift</label>
        <select
          id="shiftSelect"
          className="careShiftSelect"
          value={selectedShiftId}
          onChange={e => setSelectedShiftId(e.target.value)}
        >
          <option value="">— choose a shift —</option>
          {shifts.map(s => (
            <option key={s.docID} value={s.docID}>{formatShiftOption(s)}</option>
          ))}
        </select>
      </div>

      {selectedShift && (
        <>
          {/* Auto-populated read-only fields */}
          <div className="formSep"></div>
          <div className="careFormSection">
            <h3 className="careFormSectionTitle">Shift Details</h3>
            <div className="careFormReadOnlyGrid">
              <span className="careFormReadOnlyLabel">Caregiver</span>
              <span className="careFormReadOnlyValue">{caregiverName()}</span>

              <span className="careFormReadOnlyLabel">Shift Start</span>
              <div className="careFormTimeRow">
                <span className="careFormReadOnlyValue">
                  {formatDisplayTime(displayStart)}
                  {selectedShift.editedStart && <span className="careFormEditedBadge">edited</span>}
                </span>
                <button className="careFormEditTimeBtn" onClick={() => openEditModal('start')}>Edit</button>
              </div>

              <span className="careFormReadOnlyLabel">Shift End</span>
              <div className="careFormTimeRow">
                <span className="careFormReadOnlyValue">
                  {formatDisplayTime(displayEnd)}
                  {selectedShift.editedEnd && <span className="careFormEditedBadge">edited</span>}
                </span>
                <button className="careFormEditTimeBtn" onClick={() => openEditModal('end')}>Edit</button>
              </div>
            </div>
          </div>

          {loading && <p className="careFormLoading">Loading…</p>}

          {!loading && (
            <>
              {/* Standard fillable fields */}
              <div className="formSep"></div>
              <div className="careFormSection">
                <h3 className="careFormSectionTitle">Report Details</h3>

                <div className="careFormField">
                  <label className="careFormLabel">Mileage (miles)</label>
                  <input type="number" className="careFormInput" value={mileage}
                    onChange={e => setMileage(e.target.value)} min="0" step="0.1" />
                </div>

                <div className="careFormField">
                  <label className="careFormLabel">Supplies Description</label>
                  <input type="text" className="careFormInput" value={suppliesDesc}
                    onChange={e => setSuppliesDesc(e.target.value)} />
                </div>

                <div className="careFormField">
                  <label className="careFormLabel">Supplies Amount ($)</label>
                  <input type="number" className="careFormInput" value={suppliesAmount}
                    onChange={e => setSuppliesAmount(e.target.value)} min="0" step="0.01" />
                </div>

                <div className="careFormField">
                  <label className="careFormLabel">Important Information</label>
                  <textarea className="careFormTextarea" value={importantInfo}
                    onChange={e => setImportantInfo(e.target.value)} rows={3} />
                </div>

                <div className="careFormField">
                  <label className="careFormLabel">Tasks</label>
                  <textarea className="careFormTextarea" value={tasks}
                    onChange={e => setTasks(e.target.value)} rows={4} />
                </div>
              </div>

              {/* Custom questions */}
              {customQuestions.length > 0 && (
                <>
                  <div className="formSep"></div>
                  <div className="careFormSection">
                    <h3 className="careFormSectionTitle">Additional Questions</h3>
                    {customQuestions.map((q, i) => (
                      <div key={q.id} className="careFormField">
                        <label className="careFormLabel">
                          {i + 1}. {q.text}
                          {q.required && <span className="careFormRequired"> *</span>}
                        </label>
                        {renderQuestionInput(q)}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Receipt Photos */}
              <div className="formSep"></div>
              <div className="careFormSection">
                <h3 className="careFormSectionTitle">Receipt Photos</h3>
                <div className="careFormField">
                  <div className="careFormReceiptBtnRow">
                    <label className="careFormReceiptUploadBtn">
                      Take Photo
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="careFormReceiptInput"
                        onChange={handleReceiptSelect}
                      />
                    </label>
                    <label className="careFormReceiptUploadBtn">
                      Choose from Library
                      <input
                        type="file"
                        accept="image/*"
                        className="careFormReceiptInput"
                        onChange={handleReceiptSelect}
                      />
                    </label>
                  </div>
                </div>
                {(receiptUrls.length > 0 || pendingFiles.length > 0) && (
                  <div className="careFormReceiptGrid">
                    {receiptUrls.map((url, i) => (
                      <div key={url} className="careFormReceiptThumb">
                        <a href={url} target="_blank" rel="noreferrer">
                          <img src={url} alt={`Receipt ${i + 1}`} className="careFormReceiptImg" />
                        </a>
                      </div>
                    ))}
                    {pendingFiles.map((pf, i) => (
                      <div key={i} className="careFormReceiptThumb careFormReceiptPending">
                        <img src={pf.previewUrl} alt={`New receipt ${i + 1}`} className="careFormReceiptImg" />
                        <button
                          type="button"
                          className="careFormReceiptRemoveBtn"
                          onClick={() => removePendingFile(i)}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="formSep"></div>
              <div className="careFormSection careFormSubmitRow">
                <Button
                  onClick={handleSubmit}
                  type="button"
                  buttonStyle="btn--success--solid"
                  buttonSize="btn--medium"
                  disabled={loading}
                >
                  Save Report
                </Button>
                {statusMsg && (
                  <p className="careFormStatus" style={{ color: statusIsError ? '#eb3f27' : '#2a9d2a' }}>
                    {statusMsg}
                  </p>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Edit Time Modal */}
      {editingField && (
        <div className="careFormModalOverlay" onClick={closeEditModal}>
          <div className="careFormModal" onClick={e => e.stopPropagation()}>
            <h3 className="careFormModalTitle">
              Edit Shift {editingField === 'start' ? 'Start' : 'End'}
            </h3>
            <label className="careFormLabel">Date &amp; Time</label>
            <input
              type="datetime-local"
              className="careFormInput careFormDatetimeInput"
              value={editingValue}
              onChange={e => setEditingValue(e.target.value)}
            />
            {editError && <p className="careFormModalError">{editError}</p>}
            <div className="careFormModalActions">
              <Button
                onClick={handleSaveEditedTime}
                type="button"
                buttonStyle="btn--primary--solid"
                buttonSize="btn--medium"
                disabled={editSaving}
              >
                {editSaving ? 'Saving…' : 'Save'}
              </Button>
              <button className="careFormModalCancelBtn" onClick={closeEditModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
